import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import * as SkeletonUtils from 'three/examples/jsm/utils/SkeletonUtils.js';
import zombieGltfUrl from '../../assets/models/terror_engine_-_male_zombie.glb?url';

class SpecularGlossinessPlugin {
  constructor(parser) {
    this.name = 'KHR_materials_pbrSpecularGlossiness';
    this.parser = parser;
  }
  getMaterialType() {
    return THREE.MeshStandardMaterial;
  }
  extendMaterialParams(materialIndex, materialParams) {
    const parser = this.parser;
    const materialDef = parser.json.materials[materialIndex];
    if (!materialDef.extensions || !materialDef.extensions[this.name]) {
      return Promise.resolve();
    }
    const spec = materialDef.extensions[this.name];
    if (spec.diffuseFactor) {
      materialParams.color = new THREE.Color(spec.diffuseFactor[0], spec.diffuseFactor[1], spec.diffuseFactor[2]);
      materialParams.opacity = spec.diffuseFactor[3];
    }
    materialParams.roughness = spec.glossinessFactor !== undefined ? 1.0 - spec.glossinessFactor : 0.7;
    materialParams.metalness = 0.1;
    const pending = [];
    if (spec.diffuseTexture) {
      pending.push(parser.assignTexture(materialParams, 'map', spec.diffuseTexture));
    }
    return Promise.all(pending);
  }
}

let sharedGltf = null;

export async function loadZombieTemplate() {
  if (!sharedGltf) {
    const loader = new GLTFLoader();
    loader.register((parser) => new SpecularGlossinessPlugin(parser));
    const url = zombieGltfUrl ?? '/assets/models/terror_engine_-_male_zombie.glb';

    const loadWithTimeout = (targetUrl, ms = 25000) => {
      return Promise.race([
        loader.loadAsync(targetUrl),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error(`Timeout loading Zombie GLB (${targetUrl}) after ${ms}ms`)), ms)
        ),
      ]);
    };

    try {
      sharedGltf = await loadWithTimeout(url);
    } catch (err) {
      console.warn('[zombie] Primary load failed/timed out, retrying...', err);
      sharedGltf = await loadWithTimeout(url, 35000);
    }
  }
  return sharedGltf;
}

export class ZombieModel {
  constructor(templateGltf) {
    // Clone scene with skeleton intact
    const scene = SkeletonUtils.clone(templateGltf.scene);
    
    // Scale model to 1.2x of previous height (0.475 * 1.2 = 0.57)
    scene.scale.setScalar(0.57);
    scene.rotation.y = 0;
    
    this.group = new THREE.Group();
    this.group.name = 'zombie_character';
    this.group.add(scene);
    this.scene = scene;

    // Materials setup & disable frustum culling to guarantee visibility
    this.materials = [];
    scene.traverse((o) => {
      if (o.isMesh) {
        o.frustumCulled = false;
        if (o.material) {
          const mats = Array.isArray(o.material) ? o.material : [o.material];
          for (const m of mats) {
            m.transparent = false;
            m.depthWrite = true;
            m.opacity = 1.0;
            this.materials.push(m);
          }
        }
      }
    });

    // Native Animation Mixer setup with all 4 clips: idle, walk, attack, death
    this.mixer = new THREE.AnimationMixer(scene);
    this.actions = {};
    this.currentAction = null;
    this.currentClipName = '';

    for (const clip of templateGltf.animations ?? []) {
      const action = this.mixer.clipAction(clip);
      if (clip.name === 'death') {
        action.setLoop(THREE.LoopOnce, 1);
        action.clampWhenFinished = true;
      }
      this.actions[clip.name] = action;
    }

    // Start with default walk/idle
    this.playAnimation('walk');

    this.isAttacking = false;
    this.attackTimer = 0;
  }

  /** Fade seamlessly between native animations. */
  playAnimation(name, duration = 0.2) {
    if (this.currentClipName === name) return;
    const nextAction = this.actions[name] ?? this.actions['walk'] ?? Object.values(this.actions)[0];
    if (!nextAction) return;

    if (this.currentAction) {
      this.currentAction.fadeOut(duration);
    }
    nextAction.reset().fadeIn(duration).play();
    this.currentAction = nextAction;
    this.currentClipName = name;
  }

  /** Trigger native claw attack animation without animation clipping jitter. */
  triggerAttack() {
    if (!this.isAttacking) {
      this.isAttacking = true;
      this.attackTimer = 0.6; // Full claw/bite animation playback length
      this.playAnimation('attack', 0.1);
    }
  }

  update(dt, speed = 0, isDead = false, fallDir = -1) {
    if (isDead) {
      this.playAnimation('death', 0.15);
      if (this.mixer) this.mixer.update(dt);
      return;
    }

    if (this.isAttacking) {
      this.attackTimer -= dt;
      if (this.attackTimer <= 0) {
        this.isAttacking = false;
        this.playAnimation(speed > 0.1 ? 'walk' : 'idle', 0.15);
      }
    } else {
      if (speed > 0.1) {
        this.playAnimation('walk', 0.2);
        if (this.actions['walk']) {
          this.actions['walk'].timeScale = 1.15; // Smooth stride matching 3.84 pursuit speed
        }
      } else {
        this.playAnimation('idle', 0.2);
      }
    }

    if (this.mixer) {
      this.mixer.update(dt);
    }
  }

  dispose() {
    this.mixer?.stopAllAction();
    this.group.removeFromParent();
  }
}
